import React from 'react';
import { Box, Avatar, Badge, Tabs, Tab, Typography, Tooltip } from '@mui/material';
import { Star } from '@mui/icons-material';
import { formatDateOnly } from '../../services/adminService';

/**
 * Derive display initials.
 *
 * Falls back to the username's first character rather than rendering an empty
 * circle, because an account can exist before a name is captured.
 */
const initialsFor = (profile) => {
  const first = (profile.first_name || '').trim();
  const last = (profile.last_name || '').trim();

  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  return (profile.username || '?').charAt(0).toUpperCase();
};

// subscription_expiration arrives as a bare date (e.g. "2026-09-01"), which
// formatDateOnly anchors to local midnight — unanchored, `new Date(value)`
// would parse it as UTC midnight and render a day early in any
// negative-UTC-offset timezone.
const formatExpiration = (value) => {
  const formatted = formatDateOnly(value);
  return formatted ? `Subscriber · expires ${formatted}` : 'Subscriber';
};

/**
 * The avatar/name/tabs rail down the left of a profile page, shared by the
 * staff-facing UserDetail and the self-service Account page.
 *
 * `profile` is the canonical profile shape, not either page's raw DTO: the two
 * endpoints disagree on field names (GET /user answers `activeSubscription` and
 * `subscriptionExpiration`; GET /admin/users/:id answers `active_subscription`
 * and `subscription_expiration`) and on the type of `roles` entirely. Each page
 * adapts its own response into the snake_case shape below before handing it
 * over, so nothing here has to know which endpoint it came from:
 *
 *   { id, username, first_name, middle_name, last_name, dob, addresses,
 *     active_subscription, subscription_expiration }
 *
 * `tabs` is a list of labels rather than a fixed pair, and `idPrefix` namespaces
 * the tab/panel id attributes, because the two pages show different sections —
 * UserDetail has Permissions, Account has Security, and a Security tab must
 * never appear on a page showing someone else's profile.
 */
function ProfileIdentityRail({
  profile,
  tabs,
  idPrefix,
  ariaLabel,
  activeTab,
  onTabChange,
  isMobile,
}) {
  const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ');

  const avatar = (
    <Avatar sx={{ width: 64, height: 64, bgcolor: 'secondary.main', fontSize: '1.5rem', mx: 'auto' }}>
      {initialsFor(profile)}
    </Avatar>
  );

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : 220,
        flexShrink: 0,
        borderRight: isMobile ? 0 : 1,
        borderBottom: isMobile ? 1 : 0,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2, textAlign: 'center' }}>
        {profile.active_subscription ? (
          <Tooltip title={formatExpiration(profile.subscription_expiration)}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              aria-label="subscriber"
              badgeContent={<Star sx={{ fontSize: 14, color: 'common.white' }} />}
              sx={{ '& .MuiBadge-badge': { bgcolor: 'primary.main', height: 22, minWidth: 22, borderRadius: '50%' } }}
            >
              {avatar}
            </Badge>
          </Tooltip>
        ) : (
          avatar
        )}

        <Typography variant="subtitle1" sx={{ mt: 1.5, fontWeight: 600 }}>
          {name || 'Unnamed user'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {profile.username}
        </Typography>
      </Box>

      <Tabs
        orientation={isMobile ? 'horizontal' : 'vertical'}
        variant={isMobile ? 'scrollable' : 'standard'}
        value={activeTab}
        onChange={(event, value) => onTabChange(value)}
        aria-label={ariaLabel}
        sx={{ borderRight: 0 }}
      >
        {tabs.map((label, index) => (
          <Tab
            key={label}
            label={label}
            id={`${idPrefix}-tab-${index}`}
            aria-controls={`${idPrefix}-tabpanel-${index}`}
          />
        ))}
      </Tabs>
    </Box>
  );
}

export default ProfileIdentityRail;
